import * as React from "react";
import { observer } from "mobx-react";
import { UitkCard, UitkCardMedia, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DestinationOfferPriceInfo } from "../DestinationOfferPriceInfo/DestinationOfferPriceInfo";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { destinationOfferItem } from "../../Common/MerchCampaignCard/MerchCampaignCard";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export interface FullBleedCardProps {
  offer: Offers;
  productType: string;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  displayDate: boolean;
  displayPrice: boolean;
  moduleName: string;
  withBorder?: boolean;
}

export const FullBleedCard = observer(
  ({
    offer,
    productType,
    cardIndex,
    campaignId,
    openDialog,
    closeDialog,
    displayDate,
    displayPrice,
    moduleName,
    withBorder,
  }: FullBleedCardProps) => {
    if (offer.__typename !== "DestinationOfferItem") {
      return null;
    }

    const offerItem = destinationOfferItem(productType, offer);

    if (offerItem === null || offerItem === undefined) {
      return null;
    }

    return (
      <>
        <UitkCard
          className="FullBleedCard uitk-flex-justify-content-flex-start uitk-flex-column"
          withBorder={withBorder}
        >
          <UitkCardMedia>
            <UitkFigure ratio={UitkFigureAspectRatioType.R3_2}>
              <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
              <UitkScrim type={UitkScrimType.BOTTOM}>
                <UitkLayoutFlex justifyContent="space-between">
                  <>
                    <UitkLayoutFlexItem>
                      <div>
                        <h3 className="uitk-type-heading-500 cardContent">{offer.name}</h3>
                        {displayDate && <div className="uitk-type-paragraph-300 cardContent">{offer.dates}</div>}
                        <div className="uitk-type-paragraph-300 cardContent">{offer.secondaryContent}</div>
                      </div>
                    </UitkLayoutFlexItem>
                    <UitkLayoutFlexItem className="uitk-type-right">
                      <div>
                        {displayPrice && offerItem.price.value && (
                          <DestinationOfferPriceInfo
                            scrimDisplay={true}
                            offerPrice={offerItem.price}
                            cardIndex={cardIndex}
                            campaignId={campaignId}
                            openDialog={openDialog}
                            closeDialog={closeDialog}
                          />
                        )}
                        {displayPrice && offerItem.price.checkPriceText && (
                          <div className="uitk-scrim">
                            <a
                              className="cardContent uitk-link uitk-type-300 uitk-type-bold"
                              href={offerItem.actionLink.href}
                            >
                              {offerItem.price.checkPriceText}
                            </a>
                          </div>
                        )}
                      </div>
                    </UitkLayoutFlexItem>
                  </>
                </UitkLayoutFlex>
              </UitkScrim>
            </UitkFigure>
            <UitkCardLink>
              <TrackedLink
                moduleName={moduleName}
                action={Action.CLICK}
                href={offerItem.actionLink.href}
                rfrr={`card-${campaignId}-${cardIndex}`}
              >
                {offer.name}
              </TrackedLink>
            </UitkCardLink>
          </UitkCardMedia>
        </UitkCard>
      </>
    );
  }
);
